import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { useAnalytics } from '@/hooks/useAnalytics';
import { GenericTrackTypes } from '@/types/genericTrackTypes';
import styles from './ContentSwitcher.style';
import { BodyLargeEmphasized, BodyLargeMidEmphasis } from '../Typography';
import { FlyerType } from '@/config/constants';
import { cx } from '@emotion/css';
import colors from '@/theme/colors';
import { useState } from 'react';
import Tooltip from '@mui/material/Tooltip';

type ContentSwicherOption = {
  value: any;
  label: string;
};
interface Props {
  trackId: string;
  options: ContentSwicherOption[];
  selected: any;
  onChange: (newValue: string) => void;
  disabledTab?: FlyerType.school | FlyerType.community;
}

const SCHOOL_DISABLED_TOOLTIP = 'These type of flyers do not apply to schools.';
const COMMUNITY_DISABLED_TOOLTIP = 'These type of flyers do not apply to the community.';
export const ContentSwitcher: React.FC<Props> = ({
  trackId,
  selected,
  options,
  onChange,
  disabledTab,
}) => {
  const { track } = useAnalytics();
  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    if (disabledTab) {
      return;
    }

    if (trackId) {
      track({
        action: GenericTrackTypes.TAB_NAVIGATION,
        category: `${trackId}:switcher`,
        label: newValue,
      });
    }

    onChange(newValue);
  };

  const tooltipText =
    disabledTab === FlyerType.school ? SCHOOL_DISABLED_TOOLTIP : COMMUNITY_DISABLED_TOOLTIP;

  const renderTabWithTooltip = ({ label, value }: ContentSwicherOption) => {
    const isTabDisabled = value === disabledTab;
    return (
      <Tooltip
        title={isTabDisabled ? tooltipText : ''}
        arrow
        enterTouchDelay={0}
        componentsProps={{
          popper: {
            sx: {
              [`& .MuiTooltip-arrow`]: {
                color: colors.midnight,
              },
              [`& .MuiTooltip-tooltip`]: {
                backgroundColor: colors.midnight,
                fontSize: '14px',
                borderRadius: '5px',
              },
            },
          },
        }}
      >
        <Tab
          key={value}
          value={value}
          label={
            selected === value ? (
              <BodyLargeEmphasized>{label}</BodyLargeEmphasized>
            ) : (
              <BodyLargeMidEmphasis color={isTabDisabled ? colors.stone : colors.prussian}>
                {label}
              </BodyLargeMidEmphasis>
            )
          }
          className={styles.tab}
          data-testid={`tab${label}`}
        />
      </Tooltip>
    );
  };

  const renderTab = ({ label, value }: ContentSwicherOption) => (
    <Tab
      key={value}
      value={value}
      label={
        selected === value ? (
          <BodyLargeEmphasized>{label}</BodyLargeEmphasized>
        ) : (
          <BodyLargeMidEmphasis>{label}</BodyLargeMidEmphasis>
        )
      }
      className={styles.tab}
      data-testid={`tab${label}`}
    />
  );

  const contentSwicherTab = disabledTab ? renderTabWithTooltip : renderTab;

  return (
    <Tabs
      value={selected}
      onChange={handleChange}
      className={styles.container}
      variant="fullWidth"
      data-testid="tabHeader"
      aria-label="Flyer type switcher"
      selectionFollowsFocus
    >
      {options.map(contentSwicherTab)}

      {disabledTab && (
        <span
          className={styles.tabIndicator}
          style={selected === FlyerType.school ? { left: 0 } : { right: 0 }}
        />
      )}
    </Tabs>
  );
};
